import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Switch,
  Button,
  IconButton,
  Paper,
  Collapse,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

type ScheduleType = "DAILY" | "WEEKLY" | "MONTHLY";
type MonthlyMode = "BY_MONTHDAY" | "BY_WEEKDAY";
type Weekday = "MO" | "TU" | "WE" | "TH" | "FR" | "SA" | "SU";

const weekdayLabels: Record<Weekday, string> = {
  MO: "Пн",
  TU: "Вт",
  WE: "Ср",
  TH: "Чт",
  FR: "Пт",
  SA: "Сб",
  SU: "Вс",
};

const monthLabels = [
  { n: 1, label: "Янв" },
  { n: 2, label: "Фев" },
  { n: 3, label: "Мар" },
  { n: 4, label: "Апр" },
  { n: 5, label: "Май" },
  { n: 6, label: "Июн" },
  { n: 7, label: "Июл" },
  { n: 8, label: "Авг" },
  { n: 9, label: "Сен" },
  { n: 10, label: "Окт" },
  { n: 11, label: "Ноя" },
  { n: 12, label: "Дек" },
];

type ScheduleUiConfig = {
  type: ScheduleType;
  daily?: { interval: number };
  weekly?: { interval: number; byDay: Weekday[] };
  monthly?: {
    mode: MonthlyMode;
    byMonthDay?: number[];
    byWeekday?: { weekday: Weekday; positions: number[] };
  };
  monthsFilter?: { enabled: boolean; months: number[] };
};

export type SchedulePartConfig = {
  id: string;
  name?: string;
  dtStart?: string; // ISO date string "YYYY-MM-DD"
  untilDate?: string;
  uiConfig: ScheduleUiConfig;
  rrule: string;
};

export type ScheduleBuildResult = {
  parts: SchedulePartConfig[];
};

function uniqSorted<T>(arr: T[]) {
  return Array.from(new Set(arr));
}
function uniqSortedNumbers(arr: number[]) {
  return Array.from(new Set(arr)).sort((a, b) => a - b);
}

function buildRRule(cfg: ScheduleUiConfig): string {
  const parts: string[] = [];

  if (cfg.type === "DAILY") {
    const interval = Math.max(1, cfg.daily?.interval ?? 1);
    parts.push(`FREQ=DAILY`);
    if (interval !== 1) parts.push(`INTERVAL=${interval}`);
  }

  if (cfg.type === "WEEKLY") {
    const interval = Math.max(1, cfg.weekly?.interval ?? 1);
    const byDay = uniqSorted(cfg.weekly?.byDay ?? []);
    parts.push(`FREQ=WEEKLY`);
    if (interval !== 1) parts.push(`INTERVAL=${interval}`);
    if (byDay.length) parts.push(`BYDAY=${byDay.join(",")}`);
  }

  if (cfg.type === "MONTHLY") {
    parts.push(`FREQ=MONTHLY`);
    const mode = cfg.monthly?.mode ?? "BY_WEEKDAY";
    if (mode === "BY_MONTHDAY") {
      const days = uniqSortedNumbers(cfg.monthly?.byMonthDay ?? []).filter(
        (d) => d >= 1 && d <= 31,
      );
      if (days.length) parts.push(`BYMONTHDAY=${days.join(",")}`);
    } else {
      const weekday = cfg.monthly?.byWeekday?.weekday ?? "MO";
      const positions = uniqSortedNumbers(
        cfg.monthly?.byWeekday?.positions ?? [],
      );
      const filtered = positions.filter((p) => (p >= 1 && p <= 4) || p === -1);
      parts.push(`BYDAY=${weekday}`);
      if (filtered.length) parts.push(`BYSETPOS=${filtered.join(",")}`);
    }
  }

  const monthsFilter = cfg.monthsFilter;
  if (monthsFilter?.enabled) {
    const months = uniqSortedNumbers(monthsFilter.months).filter(
      (m) => m >= 1 && m <= 12,
    );
    if (months.length) parts.push(`BYMONTH=${months.join(",")}`);
  }

  return parts.join(";");
}

function makeDefaultUiConfig(): ScheduleUiConfig {
  return {
    type: "WEEKLY",
    weekly: { interval: 1, byDay: ["FR"] },
    monthsFilter: {
      enabled: false,
      months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    },
  };
}

function makeNewPart(): SchedulePartConfig {
  const cfg = makeDefaultUiConfig();
  return {
    id: Math.random().toString(36).slice(2),
    uiConfig: cfg,
    rrule: buildRRule(cfg),
  };
}

// ─── Single Part Editor ────────────────────────────────────────────────────

function PartEditor({
  part,
  index,
  canDelete,
  onChange,
  onDelete,
}: {
  part: SchedulePartConfig;
  index: number;
  canDelete: boolean;
  onChange: (updated: SchedulePartConfig) => void;
  onDelete: () => void;
}) {
  const cfg = part.uiConfig;
  const [expanded, setExpanded] = useState(true);

  const update = useCallback(
    (newCfg: ScheduleUiConfig) => {
      onChange({ ...part, uiConfig: newCfg, rrule: buildRRule(newCfg) });
    },
    [part, onChange],
  );

  const setType = (type: ScheduleType) => update({ ...cfg, type });

  const setDaily = (interval: number) =>
    update({ ...cfg, daily: { interval } });

  const setWeeklyInterval = (interval: number) =>
    update({ ...cfg, weekly: { ...(cfg.weekly ?? { byDay: [] }), interval } });

  const toggleWeekday = (d: Weekday) => {
    const prev = cfg.weekly?.byDay ?? [];
    const byDay = prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d];
    update({ ...cfg, weekly: { ...(cfg.weekly ?? { interval: 1 }), byDay } });
  };

  const setMonthlyMode = (mode: MonthlyMode) =>
    update({ ...cfg, monthly: { ...(cfg.monthly ?? {}), mode } });

  const toggleMonthDay = (d: number) => {
    const prev = cfg.monthly?.byMonthDay ?? [];
    const byMonthDay = prev.includes(d)
      ? prev.filter((x) => x !== d)
      : [...prev, d];
    update({
      ...cfg,
      monthly: { ...(cfg.monthly ?? { mode: "BY_MONTHDAY" }), byMonthDay },
    });
  };

  const setMonthlyWeekday = (weekday: Weekday) =>
    update({
      ...cfg,
      monthly: {
        ...(cfg.monthly ?? { mode: "BY_WEEKDAY" }),
        byWeekday: {
          ...(cfg.monthly?.byWeekday ?? { positions: [] }),
          weekday,
        },
      },
    });

  const togglePos = (p: number) => {
    const prev = cfg.monthly?.byWeekday?.positions ?? [];
    const positions = prev.includes(p)
      ? prev.filter((x) => x !== p)
      : [...prev, p];
    update({
      ...cfg,
      monthly: {
        ...(cfg.monthly ?? { mode: "BY_WEEKDAY" }),
        byWeekday: {
          ...(cfg.monthly?.byWeekday ?? { weekday: "MO" }),
          positions,
        },
      },
    });
  };

  const setMonthsEnabled = (enabled: boolean) =>
    update({
      ...cfg,
      monthsFilter: { ...(cfg.monthsFilter ?? { months: [] }), enabled },
    });

  const toggleMonth = (m: number) => {
    const prev = cfg.monthsFilter?.months ?? [];
    const months = prev.includes(m)
      ? prev.filter((x) => x !== m)
      : [...prev, m];
    update({
      ...cfg,
      monthsFilter: { ...(cfg.monthsFilter ?? { enabled: true }), months },
    });
  };

  const weeklyInvalid =
    cfg.type === "WEEKLY" && (cfg.weekly?.byDay ?? []).length === 0;
  const monthlyInvalid =
    cfg.type === "MONTHLY" &&
    ((cfg.monthly?.mode === "BY_MONTHDAY" &&
      (cfg.monthly?.byMonthDay ?? []).length === 0) ||
      (cfg.monthly?.mode === "BY_WEEKDAY" &&
        (cfg.monthly?.byWeekday?.positions ?? []).length === 0));

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        borderColor: "divider",
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1.25, bgcolor: "rgba(0,0,0,0.02)", cursor: "pointer" }}
        onClick={() => setExpanded((e) => !e)}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="subtitle2" fontWeight={600}>
            Период {index + 1}
          </Typography>
          {/*{part.dtStart || part.untilDate ? (*/}
          {/*    <Typography variant="caption" sx={{ opacity: 0.6 }}>*/}
          {/*        {part.dtStart ?? "—"} → {part.untilDate ?? "—"}*/}
          {/*    </Typography>*/}
          {/*) : null}*/}
          {!expanded && part.rrule && (
            <Typography
              variant="caption"
              sx={{
                fontFamily: "monospace",
                opacity: 0.5,
                maxWidth: 280,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {part.rrule}
            </Typography>
          )}
        </Stack>

        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          onClick={(e) => e.stopPropagation()}
        >
          {canDelete && (
            <IconButton size="small" color="error" onClick={onDelete}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton size="small" onClick={() => setExpanded((e) => !e)}>
            {expanded ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        </Stack>
      </Stack>

      <Collapse in={expanded}>
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
          {/* Date range */}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Дата начала"
              type="date"
              size="small"
              value={part.dtStart ?? ""}
              onChange={(e) =>
                onChange({ ...part, dtStart: e.target.value || undefined })
              }
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
            {/*<TextField*/}
            {/*    label="Дата окончания"*/}
            {/*    type="date"*/}
            {/*    size="small"*/}
            {/*    value={part.untilDate ?? ""}*/}
            {/*    onChange={e => onChange({ ...part, untilDate: e.target.value || undefined })}*/}
            {/*    InputLabelProps={{ shrink: true }}*/}
            {/*    sx={{ flex: 1 }}*/}
            {/*/>*/}
          </Stack>

          <Divider />

          {/* Type */}
          <FormControl>
            <Typography sx={{ fontSize: 13, opacity: 0.75, mb: 0.5 }}>
              Тип
            </Typography>
            <RadioGroup
              row
              value={cfg.type}
              onChange={(e) => setType(e.target.value as ScheduleType)}
            >
              <FormControlLabel
                value="DAILY"
                control={<Radio size="small" />}
                label="Ежедневно"
              />
              <FormControlLabel
                value="WEEKLY"
                control={<Radio size="small" />}
                label="Еженедельно"
              />
              <FormControlLabel
                value="MONTHLY"
                control={<Radio size="small" />}
                label="Ежемесячно"
              />
            </RadioGroup>
          </FormControl>

          {/* DAILY */}
          {cfg.type === "DAILY" && (
            <TextField
              label="Каждые N дней"
              type="number"
              value={cfg.daily?.interval ?? 1}
              onChange={(e) =>
                setDaily(Math.max(1, Number(e.target.value || 1)))
              }
              inputProps={{ min: 1 }}
              size="small"
              sx={{ width: 160 }}
            />
          )}

          {/* WEEKLY */}
          {cfg.type === "WEEKLY" && (
            <Box>
              <TextField
                label="Каждые N недель"
                type="number"
                value={cfg.weekly?.interval ?? 1}
                onChange={(e) =>
                  setWeeklyInterval(Math.max(1, Number(e.target.value || 1)))
                }
                inputProps={{ min: 1 }}
                size="small"
                sx={{ width: 170, mb: 1 }}
                error={weeklyInvalid}
                helperText={weeklyInvalid ? "Выберите хотя бы один день" : " "}
              />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {(Object.keys(weekdayLabels) as Weekday[]).map((d) => (
                  <Chip
                    key={d}
                    label={weekdayLabels[d]}
                    clickable
                    size="small"
                    color={
                      (cfg.weekly?.byDay ?? []).includes(d)
                        ? "primary"
                        : "default"
                    }
                    variant={
                      (cfg.weekly?.byDay ?? []).includes(d)
                        ? "filled"
                        : "outlined"
                    }
                    onClick={() => toggleWeekday(d)}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* MONTHLY */}
          {cfg.type === "MONTHLY" && (
            <Box>
              <Typography sx={{ fontSize: 13, opacity: 0.75, mb: 0.5 }}>
                Режим
              </Typography>
              <ToggleButtonGroup
                value={cfg.monthly?.mode ?? "BY_WEEKDAY"}
                exclusive
                onChange={(_, v) => v && setMonthlyMode(v)}
                size="small"
                sx={{ mb: 1.5 }}
              >
                <ToggleButton value="BY_WEEKDAY">По дню недели</ToggleButton>
                <ToggleButton value="BY_MONTHDAY">По числам</ToggleButton>
              </ToggleButtonGroup>

              {cfg.monthly?.mode === "BY_WEEKDAY" && (
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                >
                  <FormControl size="small" sx={{ width: 200 }}>
                    <InputLabel>День недели</InputLabel>
                    <Select
                      value={cfg.monthly?.byWeekday?.weekday ?? "MO"}
                      label="День недели"
                      onChange={(e) =>
                        setMonthlyWeekday(e.target.value as Weekday)
                      }
                    >
                      {(Object.keys(weekdayLabels) as Weekday[]).map((d) => (
                        <MenuItem key={d} value={d}>
                          {weekdayLabels[d]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {[1, 2, 3, 4, -1].map((p) => (
                      <Chip
                        key={p}
                        label={p === -1 ? "последняя" : `${p}-я`}
                        clickable
                        size="small"
                        color={
                          (cfg.monthly?.byWeekday?.positions ?? []).includes(p)
                            ? "primary"
                            : "default"
                        }
                        variant={
                          (cfg.monthly?.byWeekday?.positions ?? []).includes(p)
                            ? "filled"
                            : "outlined"
                        }
                        onClick={() => togglePos(p)}
                      />
                    ))}
                  </Stack>
                </Stack>
              )}

              {cfg.monthly?.mode === "BY_MONTHDAY" && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                    <Chip
                      key={d}
                      label={String(d)}
                      clickable
                      size="small"
                      color={
                        (cfg.monthly?.byMonthDay ?? []).includes(d)
                          ? "primary"
                          : "default"
                      }
                      variant={
                        (cfg.monthly?.byMonthDay ?? []).includes(d)
                          ? "filled"
                          : "outlined"
                      }
                      onClick={() => toggleMonthDay(d)}
                    />
                  ))}
                </Stack>
              )}

              {monthlyInvalid && (
                <Typography color="error" sx={{ fontSize: 12, mt: 0.5 }}>
                  Выберите хотя бы одно значение
                </Typography>
              )}
            </Box>
          )}

          <Divider />

          {/* Months filter */}
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <Box>
              <Typography sx={{ fontSize: 13, opacity: 0.75 }}>
                Месяцы действия
              </Typography>
              <Typography sx={{ fontSize: 12, opacity: 0.65 }}>
                Можно сделать "кроме июля/августа", выбрав остальные месяцы
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={cfg.monthsFilter?.enabled ?? false}
                  onChange={(e) => setMonthsEnabled(e.target.checked)}
                  size="small"
                />
              }
              label={
                cfg.monthsFilter?.enabled ? "Только выбранные" : "Во все месяцы"
              }
            />
          </Stack>

          {cfg.monthsFilter?.enabled && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {monthLabels.map((m) => (
                <Chip
                  key={m.n}
                  label={m.label}
                  clickable
                  size="small"
                  color={
                    (cfg.monthsFilter?.months ?? []).includes(m.n)
                      ? "primary"
                      : "default"
                  }
                  variant={
                    (cfg.monthsFilter?.months ?? []).includes(m.n)
                      ? "filled"
                      : "outlined"
                  }
                  onClick={() => toggleMonth(m.n)}
                />
              ))}
            </Stack>
          )}

          {/* RRule preview */}
          {/*<Box sx={{ p: 1.25, borderRadius: 2, bgcolor: "rgba(0,0,0,0.04)" }}>
                        <Typography sx={{ fontSize: 12, opacity: 0.7 }}>RRULE (предпросмотр)</Typography>
                        <Typography sx={{ fontFamily: "monospace", fontSize: 12 }}>
                            {part.rrule || "—"}
                        </Typography>
                    </Box>*/}
        </Box>
      </Collapse>
    </Paper>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────

export default function ScheduleBuilder(props: {
  value?: SchedulePartConfig[];
  onChange?: (result: ScheduleBuildResult) => void;
}) {
  const [parts, setParts] = useState<SchedulePartConfig[]>(
    props.value?.length ? props.value : [makeNewPart()],
  );

  useEffect(() => {
    if (!props.value) return;

    // если прилетело пусто — оставим хотя бы один период
    if (props.value.length === 0) {
      setParts([makeNewPart()]);
      return;
    }

    setParts(props.value);
  }, [props.value]);

  const addPart = () => setParts((prev) => [...prev, makeNewPart()]);

  const updatePart = useCallback((id: string, updated: SchedulePartConfig) => {
    setParts((prev) => prev.map((p) => (p.id === id ? updated : p)));
  }, []);

  const deletePart = useCallback((id: string) => {
    setParts((prev) => prev.filter((p) => p.id !== id));
  }, []);

  useEffect(() => {
    props.onChange?.({ parts });
  }, [parts, props]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="subtitle1" fontWeight={600}>
        График вывоза
      </Typography>

      {parts.map((part, index) => (
        <PartEditor
          key={part.id}
          part={part}
          index={index}
          canDelete={parts.length > 1}
          onChange={(updated) => updatePart(part.id, updated)}
          onDelete={() => deletePart(part.id)}
        />
      ))}

      <Button
        startIcon={<AddIcon />}
        variant="outlined"
        onClick={addPart}
        sx={{ alignSelf: "flex-start" }}
      >
        Добавить период
      </Button>
    </Box>
  );
}
